import {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FC,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from "react";
import { createPortal } from "react-dom";
import { Plus, Pencil } from "lucide-react";
import { normalizeForSearch, parseSearchTerms } from "@/utils/multiTermSearch";
import "@/components/ui/SearchableSelect.css";

// ─────────────────────────────────────────────────────────────
// Companion types (file-local; exported for optional consumer use)
// ─────────────────────────────────────────────────────────────

/**
 * Shape of each selectable option.
 * `value` is typed as `unknown` to support consumers with polymorphic IDs
 * (string ID, UUID, number, composite keys).
 */
export interface SearchableOption {
  value: unknown;
  label: string;
  // Optional fields supported by existing call sites:
  searchText?: string;
  // Internal marker for the "clear selection" synthetic option:
  _isClearOption?: boolean;
  // Allow arbitrary extra data used by consumers in formatOptionLabel/formatValue:
  [key: string]: unknown;
}

/**
 * Imperative API exposed via `selectRef` (useImperativeHandle).
 */
export interface SearchableHandle {
  focus(): void;
  open(): void;
  close(): void;
}

/**
 * Style record used for the dropdown panel. Accepts CSSProperties values
 * plus raw strings/numbers for CSS custom properties (e.g. `var(--z-…)`).
 */
type DropdownStyle = CSSProperties & Record<string, string | number | undefined>;

/**
 * SearchableSelect
 * Props:
 * - options: Array<{ value:any, label:string }>
 * - value: { value, label } | null
 * - onChange: (opt|null) => void
 * - placeholder?: string
 * - isDisabled?: boolean
 * - onAddNew?: (searchText?: string) => void - Callback para adicionar novo item (mostra botão no dropdown)
 * - addNewLabel?: string - Label do botão de adicionar (default: 'Adicionar novo...')
 * - showAddNewOnlyWhenEmpty?: boolean - Se true, mostra botão apenas quando há texto digitado OU não há resultados (default: false = mostra sempre)
 * - onEditItem?: (item) => void - Callback para editar um item existente (mostra ícone de edição)
 * - tabIndex?: number - Tab index para navegação por teclado
 * - autoOpen?: boolean - Se true, abre o dropdown automaticamente ao montar
 * - onSelect?: () => void - Callback chamado após selecionar um item (para navegação)
 * - selectRef?: React.Ref - Ref para expor métodos do componente (focus, open)
 * - initialQuery?: string - Texto inicial de busca (preenchido automaticamente ao abrir)
 * - allowClear?: boolean - Se true, adiciona opção para limpar seleção (salvar como null)
 * - clearLabel?: string - Label da opção de limpar (default: '(Nenhum)')
 * - formatOptionLabel?: (option) => ReactNode - Função para customizar renderização de cada opção no dropdown
 * - formatValue?: (option) => ReactNode - Função para customizar renderização do valor selecionado
 */
export interface SearchableSelectProps {
  options?: SearchableOption[];
  value?: SearchableOption | null;
  onChange?: (opt: SearchableOption | null) => void;
  placeholder?: string;
  isDisabled?: boolean;
  onAddNew?: ((searchText?: string) => void) | null;
  addNewLabel?: string;
  showAddNewOnlyWhenEmpty?: boolean;
  onEditItem?: ((item: SearchableOption) => void) | null;
  tabIndex?: number;
  autoOpen?: boolean;
  onSelect?: (() => void) | null;
  selectRef?: Ref<SearchableHandle> | null;
  initialQuery?: string;
  allowClear?: boolean;
  clearLabel?: string;
  formatOptionLabel?: ((option: SearchableOption) => ReactNode) | null;
  formatValue?: ((option: SearchableOption | null) => ReactNode) | null;
}

const SearchableSelect: FC<SearchableSelectProps> = ({
  options = [],
  value = null,
  onChange,
  placeholder = "Selecione...",
  isDisabled = false,
  onAddNew = null,
  addNewLabel = "Adicionar novo...",
  showAddNewOnlyWhenEmpty = false,
  onEditItem = null,
  tabIndex,
  autoOpen = false,
  onSelect = null,
  selectRef = null,
  initialQuery = "",
  allowClear = false,
  clearLabel = "(Nenhum)",
  formatOptionLabel = null,
  formatValue = null,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [q, setQ] = useState<string>(initialQuery);
  const [hoveredItem, setHoveredItem] = useState<unknown>(null);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [dropdownStyle, setDropdownStyle] = useState<DropdownStyle>({});
  const panelRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Expor métodos via ref
  useImperativeHandle(selectRef, (): SearchableHandle => ({
    focus: () => {
      buttonRef.current?.focus();
      // Abrir dropdown automaticamente ao receber foco
      if (!isDisabled) {
        setTimeout(() => setOpen(true), 50);
      }
    },
    open: () => setOpen(true),
    close: () => setOpen(false),
  }));

  // AutoOpen - abre automaticamente ao montar
  useEffect(() => {
    if (autoOpen && !isDisabled) {
      // Pequeno delay para garantir que o componente está montado
      const timer = setTimeout(() => {
        setOpen(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoOpen, isDisabled]);

  // Atualizar q quando initialQuery mudar (para quando componente remonta)
  useEffect(() => {
    setQ(initialQuery);
  }, [initialQuery]);

  // Opção de limpar (quando allowClear está habilitado)
  const clearOption = useMemo<SearchableOption | null>(
    () =>
      allowClear
        ? { value: null, label: clearLabel, _isClearOption: true }
        : null,
    [allowClear, clearLabel],
  );

  const filtered = useMemo<SearchableOption[]>(() => {
    // Usa utilitário centralizado para normalização e parsing de termos
    const searchTerms = parseSearchTerms(q);

    // Se não há busca, retorna todas as opções (com clear no início se habilitado)
    if (searchTerms.length === 0) {
      return clearOption ? [clearOption, ...options] : options;
    }

    const filteredOptions = options.filter((o) => {
      // Obtém o texto para busca (searchText ou label normalizado)
      const searchTarget = o.searchText
        ? normalizeForSearch(o.searchText)
        : normalizeForSearch(o.label || "");

      // Verifica se TODOS os termos estão presentes (AND logic)
      return searchTerms.every((term) => searchTarget.includes(term));
    });

    // Inclui opção de limpar no início se habilitado e se o clearLabel contém a busca
    if (clearOption) {
      const clearLabelNormalized = normalizeForSearch(clearLabel);
      const matchesClear = searchTerms.every((term) =>
        clearLabelNormalized.includes(term),
      );
      if (matchesClear) {
        return [clearOption, ...filteredOptions];
      }
    }

    return filteredOptions;
  }, [options, q, clearOption, clearLabel]);

  // Quando o dropdown abre, destacar o primeiro item
  useEffect(() => {
    if (open) {
      setHighlightedIndex(filtered.length > 0 ? 0 : -1);
    }
  }, [open, filtered.length]);

  // Fecha ao clicar fora / Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setQ("");
        setHighlightedIndex(-1);
        setDropdownStyle({});
      }
    };
    const onClick = (e: globalThis.MouseEvent) => {
      const target = e.target as Node | null;
      // Verificar se o clique foi no painel do dropdown (via portal)
      const isOnPanel = target ? panelRef.current?.contains(target) : false;
      const isOnButton = target ? buttonRef.current?.contains(target) : false;
      // Verificar se o clique foi em QUALQUER dropdown portal (pode haver múltiplos)
      const targetEl = target as Element | null;
      const isOnAnyPortal =
        targetEl && typeof targetEl.closest === "function"
          ? targetEl.closest(".ss-panel-portal")
          : null;

      if (!isOnPanel && !isOnButton && !isOnAnyPortal) {
        setOpen(false);
        setQ("");
        setHighlightedIndex(-1);
        setDropdownStyle({});
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const selectItem = (opt: SearchableOption) => {
    // Se é a opção de limpar, passa null para o onChange
    if (opt?._isClearOption) {
      onChange?.(null);
    } else {
      onChange?.(opt);
    }
    setOpen(false);
    setQ("");
    setHighlightedIndex(-1);
    setDropdownStyle({});
    // Chamar onSelect após selecionar (para navegação entre campos)
    if (onSelect) {
      setTimeout(() => onSelect(), 50);
    }
  };

  const handleAddNew = () => {
    const searchText = q.trim();
    setOpen(false);
    setQ("");
    setDropdownStyle({});
    if (onAddNew) {
      onAddNew(searchText);
    }
  };

  const handleEditItem = (
    e: MouseEvent<HTMLButtonElement>,
    item: SearchableOption,
  ) => {
    e.stopPropagation(); // Não seleciona o item ao clicar no ícone de edição
    setOpen(false);
    setQ("");
    setDropdownStyle({});
    if (onEditItem) {
      onEditItem(item);
    }
  };

  // Calcular posição do dropdown quando abre
  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 320; // altura aproximada do dropdown
      const openUpward =
        spaceBelow < dropdownHeight && rect.top > dropdownHeight;

      const newStyle: DropdownStyle = {
        position: "fixed",
        left: `${rect.left}px`,
        width: `${Math.max(rect.width, 280)}px`,
        maxWidth: "450px",
        zIndex: "var(--z-select-dropdown)",
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        padding: "8px",
        boxShadow:
          "0 10px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)",
      };

      if (openUpward) {
        newStyle.bottom = `${window.innerHeight - rect.top + 6}px`;
      } else {
        newStyle.top = `${rect.bottom + 6}px`;
      }

      setDropdownStyle(newStyle);
    }
  }, [open]);

  // Atualizar posição ao fazer scroll ou resize
  useEffect(() => {
    if (!open) return;

    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const dropdownHeight = 320;
        const openUpward =
          spaceBelow < dropdownHeight && rect.top > dropdownHeight;

        const newStyle: DropdownStyle = {
          position: "fixed",
          left: `${rect.left}px`,
          width: `${Math.max(rect.width, 280)}px`,
          maxWidth: "450px",
          zIndex: "var(--z-select-dropdown)",
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "8px",
          boxShadow:
            "0 10px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)",
        };

        if (openUpward) {
          newStyle.bottom = `${window.innerHeight - rect.top + 6}px`;
        } else {
          newStyle.top = `${rect.bottom + 6}px`;
        }

        setDropdownStyle(newStyle);
      }
    };

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  // Conteúdo do dropdown - só renderiza quando posição foi calculada
  const dropdownContent = open && dropdownStyle.position && (
    <div
      ref={panelRef}
      className="ss-panel ss-panel-portal"
      style={dropdownStyle as CSSProperties}
      onMouseDown={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
    >
      <input
        ref={inputRef}
        className="ss-search"
        autoFocus
        placeholder="Pesquisar..."
        value={q}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setQ(e.target.value);
          setHighlightedIndex(0); // Reset para o primeiro item ao digitar
        }}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
          // Limite de itens visíveis para performance
          const maxVisible = 200;
          const visibleCount = Math.min(filtered.length, maxVisible);
          // Verificar se o botão de adicionar está visível
          // showAddNewOnlyWhenEmpty=false: mostra sempre | showAddNewOnlyWhenEmpty=true: mostra quando tem texto OU sem resultados
          const showAddButton =
            onAddNew &&
            (!showAddNewOnlyWhenEmpty || q.trim() || filtered.length === 0);
          // Total de itens navegáveis (incluindo o botão de adicionar se visível)
          const totalItems = visibleCount + (showAddButton ? 1 : 0);
          // Índice do botão de adicionar (último item visível)
          const addButtonIndex = visibleCount;

          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex((prev) =>
              Math.min(prev + 1, totalItems - 1),
            );
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex((prev) => Math.max(prev - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            if (highlightedIndex >= 0) {
              if (highlightedIndex === addButtonIndex && showAddButton) {
                // Clicou no botão de adicionar
                handleAddNew();
              } else if (filtered[highlightedIndex]) {
                // Clicou em um item normal
                selectItem(filtered[highlightedIndex]);
              }
            }
          } else if (e.key === "Tab") {
            // Se tem apenas um resultado ou o primeiro está selecionado, seleciona e fecha
            if (filtered.length === 1) {
              e.preventDefault();
              selectItem(filtered[0]);
            } else if (
              highlightedIndex >= 0 &&
              filtered[highlightedIndex]
            ) {
              e.preventDefault();
              selectItem(filtered[highlightedIndex]);
            }
          }
        }}
      />
      <div className="ss-list" role="listbox">
        {/* Nada encontrado - mostra apenas se não tiver opção de adicionar */}
        {filtered.length === 0 && !onAddNew && (
          <div className="ss-empty">Nada encontrado</div>
        )}
        {/* Aviso de muitos resultados - digite para filtrar */}
        {filtered.length > 200 && !q.trim() && (
          <div
            className="ss-empty"
            style={{ color: "#6b7280", fontSize: "12px", padding: "8px 12px" }}
          >
            {filtered.length} resultados. Digite para filtrar...
          </div>
        )}
        {/* Lista de resultados - limita a 200 para performance */}
        {filtered.length > 0 &&
          filtered.slice(0, 200).map((o, index) => {
            const selected =
              value && String(value.value) === String(o.value);
            const isHovered = hoveredItem === o.value;
            const isHighlighted = index === highlightedIndex;
            const isClearOption = o._isClearOption;
            return (
              <div
                key={isClearOption ? "_clear_" : String(o.value)}
                role="option"
                aria-selected={selected ?? false}
                className={`ss-item${isHighlighted ? " ss-item-highlighted" : ""}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                  background: isHighlighted ? "#dbeafe" : undefined,
                  ...(isClearOption && {
                    color: "#6b7280",
                    fontStyle: "italic",
                    borderBottom: "1px solid #e5e7eb",
                    marginBottom: "4px",
                    paddingBottom: "10px",
                  }),
                }}
                onClick={() => selectItem(o)}
                onMouseEnter={() => {
                  setHoveredItem(o.value);
                  setHighlightedIndex(index);
                }}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <span
                  style={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {formatOptionLabel ? formatOptionLabel(o) : o.label}
                </span>
                {onEditItem && isHovered && !isClearOption && (
                  <button
                    type="button"
                    onClick={(e: MouseEvent<HTMLButtonElement>) =>
                      handleEditItem(e, o)
                    }
                    style={{
                      padding: "2px 4px",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#6b7280",
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={(e: MouseEvent<HTMLButtonElement>) => {
                      e.currentTarget.style.background = "#e5e7eb";
                      e.currentTarget.style.color = "#3b82f6";
                    }}
                    onMouseLeave={(e: MouseEvent<HTMLButtonElement>) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = "#6b7280";
                    }}
                    title="Editar"
                  >
                    <Pencil size={14} />
                  </button>
                )}
              </div>
            );
          })}
      </div>
      {/* Botão para adicionar novo item */}
      {/* showAddNewOnlyWhenEmpty=false: mostra sempre | showAddNewOnlyWhenEmpty=true: mostra quando tem texto OU sem resultados */}
      {onAddNew &&
        (!showAddNewOnlyWhenEmpty || q.trim() || filtered.length === 0) &&
        (() => {
          const visibleCount = Math.min(filtered.length, 200);
          const isAddButtonHighlighted = highlightedIndex === visibleCount;
          return (
            <div
              className="ss-add-new"
              onClick={handleAddNew}
              onMouseEnter={() => setHighlightedIndex(visibleCount)}
              style={{
                padding: "10px 12px",
                fontSize: "13px",
                cursor: "pointer",
                color: "#3b82f6",
                fontWeight: "500",
                borderTop: filtered.length > 0 ? "1px solid #e5e7eb" : "none",
                background: isAddButtonHighlighted ? "#dbeafe" : "#f9fafb",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "background 0.15s",
                borderRadius: "0 0 8px 8px",
              }}
            >
              <Plus size={14} />
              {q.trim() ? `${addNewLabel} "${q.trim()}"` : addNewLabel}
            </div>
          );
        })()}
    </div>
  );

  return (
    <div className="ss">
      <button
        ref={buttonRef}
        type="button"
        className="ss-toggle"
        onClick={() => setOpen((s) => !s)}
        onKeyDown={(e: KeyboardEvent<HTMLButtonElement>) => {
          // Abre o dropdown ao pressionar Enter, Space ou qualquer letra/número para busca rápida
          if (
            !open &&
            (e.key === "Enter" || e.key === " " || e.key.length === 1)
          ) {
            e.preventDefault();
            setOpen(true);
            // Se pressionou uma letra, inicia a busca com ela
            if (e.key.length === 1 && e.key !== " ") {
              setQ(e.key);
              setHighlightedIndex(0);
            }
          }
        }}
        disabled={isDisabled}
        tabIndex={tabIndex}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {value ? (
          <span>{formatValue ? formatValue(value) : value.label}</span>
        ) : (
          <span className="ss-placeholder">{placeholder}</span>
        )}
      </button>

      {/* Renderiza o dropdown via Portal para evitar problemas de overflow */}
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
};

// Avoid unused-import warnings when some React type aliases are only referenced via value space.
// (kept for future extension: FocusEvent imported for onFocus/onBlur narrow typing if needed.)
export type _SearchableSelectFocusEvent = FocusEvent<HTMLDivElement>;

export default SearchableSelect;
